import { useState, useEffect } from 'react';
import { Layout } from '../components/common/Layout';
import { bookRecordService } from '../services/bookRecordService';
import { bookService } from '../services/bookService';
import type { BookRecord, Book, RecordStatus } from '../types';
import { sortBookRecords } from '../utils/records';
import { IssueBookModal, ReturnBookModal } from '../components/admin/BookRecordModals';
import { getImageUrl } from '../utils/imageHelper';
import './AssigneesPage.css';

const ITEMS_PER_PAGE = 15;

export const AssigneesPage = () => {
  const [records, setRecords] = useState<BookRecord[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<BookRecord | null>(null);
  const [statusFilter, setStatusFilter] = useState<RecordStatus | ''>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [recordsData, booksData] = await Promise.all([
        bookRecordService.getAllRecords({ limit: 1000 }),
        bookService.getAllBooks({ limit: 1000 }),
      ]);
      setRecords(recordsData);
      setBooks(booksData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReturn = (record: BookRecord) => {
    setSelectedRecord(record);
    setShowReturnModal(true);
  };

  const handleIssueSaved = (record: BookRecord) => {
    setRecords([...records, record]);
    setShowIssueModal(false);
  };

  const handleReturnSaved = (record: BookRecord) => {
    setRecords(records.map(r => (r.id === record.id ? record : r)));
    setShowReturnModal(false);
    setSelectedRecord(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  const filteredRecords = sortBookRecords(
    records.filter((record) => {
    // Comprehensive search: borrower name, book title, book ID, dates, contact info, notes, status
    const book = books.find(b => b.id === record.book_id);

    if (!searchTerm) {
      const matchesStatus = !statusFilter || record.status === statusFilter;
      return matchesStatus;
    }

    const normalizedSearch = searchTerm.toLowerCase().trim();

    // Search in record fields
    const borrowerNameMatch = record.borrower_name?.toLowerCase().includes(normalizedSearch);
    const contactInfoMatch = record.contact_info?.toLowerCase().includes(normalizedSearch);
    const bookTitleMatch = record.book_title?.toLowerCase().includes(normalizedSearch);
    const bookSnMatch = record.book_sn?.toLowerCase().includes(normalizedSearch);
    const bookIdMatch = String(record.book_id).includes(normalizedSearch);
    const notesMatch = record.notes?.toLowerCase().includes(normalizedSearch);
    const statusMatch = record.status?.toLowerCase().includes(normalizedSearch);

    // Search in dates (formatted)
    const borrowedDateMatch = formatDate(record.borrowed_date).includes(normalizedSearch);
    const returnDateMatch = record.return_date ? formatDate(record.return_date).includes(normalizedSearch) : false;
    
    // Search in book details if available
    const bookTitleFromBook = book?.title?.toLowerCase().includes(normalizedSearch);
    const bookAuthorMatch = book?.author_name?.toLowerCase().includes(normalizedSearch);
    const bookCategoryMatch = book?.category?.toLowerCase().includes(normalizedSearch);
    const bookIsbnMatch = book?.isbn?.toLowerCase().includes(normalizedSearch);
    
    const matchesSearch = borrowerNameMatch || contactInfoMatch || bookTitleMatch || 
                          bookSnMatch || bookIdMatch || notesMatch || statusMatch ||
                          borrowedDateMatch || returnDateMatch ||
                          bookTitleFromBook || bookAuthorMatch || bookCategoryMatch || bookIsbnMatch;
    
    const matchesStatus = !statusFilter || record.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  }),
    statusFilter
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredRecords.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getBookTitle = (record: BookRecord) => {
    return record.book_title || record.book_sn || `Book ID: ${record.book_id}`;
  };

  if (isLoading) {
    return (
      <Layout isAdmin>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading records...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout isAdmin>
      <div className="assignees-page">
        {/* Page Header */}
        <div className="page-header">
          <div className="header-content">
            <h1 className="page-title">Borrowing Records</h1>
            <p className="page-subtitle">
              Manage book circulation and handle returns.
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              setSelectedRecord(null);
              setShowIssueModal(true);
            }}
          >
            <span className="material-symbols-outlined">add_circle</span>
            <span>Issue New Book</span>
          </button>
        </div>

        {/* Filters */}
        <div className="filters-bar">
          <div className="filter-tabs">
            <button
              className={`filter-tab ${statusFilter === '' ? 'active' : ''}`}
              onClick={() => setStatusFilter('')}
            >
              All Records
            </button>
            <button
              className={`filter-tab ${statusFilter === 'borrowed' ? 'active' : ''}`}
              onClick={() => setStatusFilter('borrowed')}
            >
              Borrowed
            </button>
            <button
              className={`filter-tab ${statusFilter === 'returned' ? 'active' : ''}`}
              onClick={() => setStatusFilter('returned')}
            >
              Returned
            </button>
          </div>
          <div className="search-wrapper">
            <span className="material-symbols-outlined">search</span>
            <input
              type="text"
              className="form-input"
              placeholder="Search by borrower, book title, author, contact, ID, or date..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>SN</th>
                  <th>Cover</th>
                  <th>Title</th>
                  <th>Borrower Name</th>
                  <th>Issued Date</th>
                  <th>Return Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRecords.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="empty-state">
                      No records found
                    </td>
                  </tr>
                ) : (
                  paginatedRecords.map((record, index) => {
                    const book = books.find(b => b.id === record.book_id);
                    const coverUrl = getImageUrl(book?.image_path);
                    return (
                      <tr key={record.id}>
                        <td className="font-medium">{index + 1}</td>
                        <td className="book-cover-column">
                          {coverUrl ? (
                            <img
                              src={coverUrl}
                              alt="Book cover"
                              className="book-cover-inline"
                              onError={(e) => {
                                const target = e.currentTarget as HTMLImageElement;
                                target.style.display = 'none';
                                const placeholder = target.nextElementSibling as HTMLElement;
                                if (placeholder) {
                                  placeholder.style.display = 'flex';
                                }
                              }}
                            />
                          ) : null}
                          <div className="book-cover-placeholder-inline" style={{ display: coverUrl ? 'none' : 'flex' }} title={`${getBookTitle(record)} - ${record.borrower_name}`}>
                            <span className="material-symbols-outlined">menu_book</span>
                          </div>
                        </td>
                        <td className="book-title-column">
                          <span className="book-title-text">{getBookTitle(record)}</span>
                        </td>
                        <td className="font-medium">{record.borrower_name}</td>
                        <td className="text-sub">{formatDate(record.borrowed_date)}</td>
                        <td className="text-sub">{record.return_date ? formatDate(record.return_date) : '-'}</td>
                        <td>
                          <span className={`status-badge status-${record.status}`}>
                            {record.status}
                          </span>
                        </td>
                        <td>
                          <div className="actions">
                            {record.status === 'borrowed' ? (
                              <button
                                className="btn btn-ghost action-btn return"
                                onClick={() => handleReturn(record)}
                              >
                                <span className="material-symbols-outlined">assignment_return</span>
                                <span>Return</span>
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="table-footer">
            <p className="pagination-info">
              Showing <strong>{filteredRecords.length === 0 ? 0 : startIndex + 1}</strong> to{' '}
              <strong>{Math.min(endIndex, filteredRecords.length)}</strong> of{' '}
              <strong>{filteredRecords.length}</strong> results
            </p>
            <div className="pagination-buttons">
              <button 
                className="btn btn-ghost" 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <button 
                className="btn btn-ghost"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          </div>
        </div>

        {showIssueModal && (
          <IssueBookModal
            books={books}
            existingRecords={records}
            onClose={() => setShowIssueModal(false)}
            onSaved={handleIssueSaved}
          />
        )}

        {showReturnModal && selectedRecord && (
          <ReturnBookModal
            record={selectedRecord}
            onClose={() => {
              setShowReturnModal(false);
              setSelectedRecord(null);
            }}
            onSaved={handleReturnSaved}
          />
        )}
      </div>
    </Layout>
  );
};
